import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import styled, { keyframes } from 'styled-components';

// Subtle animations for the background
const pulse = keyframes`
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.02); }
`;

const drift = keyframes`
  0% { transform: translate(0, 0); }
  25% { transform: translate(10px, -10px); }
  50% { transform: translate(0, 0); }
  75% { transform: translate(-10px, 10px); }
  100% { transform: translate(0, 0); }
`;

const slowDrift = keyframes`
  0% { transform: translate(0, 0); }
  25% { transform: translate(-15px, 5px); }
  50% { transform: translate(0, 0); }
  75% { transform: translate(15px, -5px); }
  100% { transform: translate(0, 0); }
`;

const BackgroundWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: -10;
  overflow: hidden;
`;

const GradientBackground = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ $gradient }) => $gradient};
  transition: background 0.5s ease;
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 600px;
  background: var(--accent);
  mix-blend-mode: ${({ $isDark }) => $isDark ? 'overlay' : 'multiply'};
  filter: blur(80px);
  opacity: ${({ $isDark, $primary }) => 
    $isDark 
      ? $primary ? '0.15' : '0.08'
      : $primary ? '0.12' : '0.08'};
  transition: background 0.3s ease;
  animation: ${({ $primary }) => $primary ? pulse : slowDrift} ${({ $primary }) => $primary ? '15s' : '25s'} infinite ease-in-out;
  
  ${({ $primary }) => $primary 
    ? 'top: -300px; left: -300px;' 
    : 'bottom: -300px; right: -300px;'}
`;

// Additional decorative orbs for subtle movement
const SmallOrb = styled.div`
  position: absolute;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size}px;
  border-radius: 50%;
  background: ${({theme, $color}) => $color || theme.colors.accent};
  opacity: ${({$opacity}) => $opacity || 0.05};
  filter: blur(30px);
  top: ${({$top}) => $top};
  left: ${({$left}) => $left};
  right: ${({$right}) => $right};
  bottom: ${({$bottom}) => $bottom};
  animation: ${drift} ${({$duration}) => $duration || '40s'} infinite ease-in-out;
  z-index: -1;
`;

// Function to blend colors
const blendColors = (color1, color2, percentage) => {
  // Convert from Hex to RGB
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const formattedHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(formattedHex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Convert from RGB to Hex
  const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return color1;
  
  const blend = (c1, c2) => Math.round(c1 * (percentage / 100) + c2 * (1 - percentage / 100));
  
  return rgbToHex(
    blend(rgb1.r, rgb2.r),
    blend(rgb1.g, rgb2.g),
    blend(rgb1.b, rgb2.b)
  );
};

const AnimatedBackground = () => {
  const { isDark, customColors } = useTheme();
  const [mounted, setMounted] = useState(false);
  
  // Generate the gradient based on the current theme
  const gradient = useMemo(() => {
    if (!mounted) return '';
    
    const accent = customColors.accent || '#ffa600';
    
    // Fixed base colors for blending
    const darkBaseColor = '#452800';
    const lightBaseColor = '#fef3c7';
    
    // Blend accent color with the base color
    const bottomRightColor = isDark 
      ? blendColors(accent, darkBaseColor, 30)
      : blendColors(accent, lightBaseColor, 20);
    
    return isDark 
      ? `linear-gradient(to bottom right, ${customColors.bg}, #1E293B, ${bottomRightColor})`
      : `linear-gradient(to bottom right, ${customColors.bg}, #e2e8f0, ${bottomRightColor})`;
  }, [isDark, customColors, mounted]);
  
  // Only render once component is mounted to improve initial load time
  useEffect(() => {
    setMounted(true);
  }, []);
  
  if (!mounted) return <BackgroundWrapper><GradientBackground $gradient="" /></BackgroundWrapper>;

  // Create an array of small orbs with random positions
  const smallOrbs = [
    { size: 200, top: '25%', left: '35%', opacity: 0.04, duration: '45s', color: customColors.accent },
    { size: 150, top: '70%', left: '65%', opacity: 0.03, duration: '55s', color: customColors.secondary },
    { size: 180, top: '40%', right: '10%', opacity: 0.025, duration: '60s', color: customColors.accent },
    { size: 120, bottom: '15%', left: '20%', opacity: 0.035, duration: '50s', color: customColors.secondary }
  ];

  return (
    <BackgroundWrapper>
      <GradientBackground $gradient={gradient}>
        <FloatingOrb $isDark={isDark} $primary />
        <FloatingOrb $isDark={isDark} />
        
        {/* Additional decorative orbs */}
        {smallOrbs.map((orb, index) => (
          <SmallOrb 
            key={index}
            $size={orb.size}
            $top={orb.top}
            $left={orb.left}
            $right={orb.right}
            $bottom={orb.bottom}
            $opacity={orb.opacity}
            $duration={orb.duration}
            $color={orb.color}
          />
        ))}
      </GradientBackground>
    </BackgroundWrapper>
  );
};

export default AnimatedBackground;